import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Confidence indicator component for comparison views
export const ConfidenceIndicator = ({ level, showLabel = false, className }) => {
  const config = {
    vendor: {
      label: 'Vendor-provided',
      description: 'From official vendor documentation',
      dotColor: 'bg-primary',
      textColor: 'text-primary',
    },
    reviewed: {
      label: 'Independently reviewed',
      description: 'Verified through independent sources',
      dotColor: 'bg-success',
      textColor: 'text-success',
    },
    unclear: {
      label: 'Information unclear',
      description: 'Could not be independently verified',
      dotColor: 'bg-warning',
      textColor: 'text-warning',
    },
  };

  const { label, description, dotColor, textColor } = config[level] || config.vendor;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex items-center gap-1.5 cursor-help', className)}>
            <span className={cn('w-1.5 h-1.5 rounded-full', dotColor)} />
            {showLabel && (
              <span className={cn('text-xs', textColor)}>{label}</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-medium text-sm">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Section label for distinguishing facts from interpretation
export const DataTypeLabel = ({ type, className }) => {
  const config = {
    fact: {
      label: 'Factual information',
      description: 'Data points from vendor documentation and official sources',
      bgColor: 'bg-muted/50',
      borderColor: 'border-border',
      icon: null,
    },
    interpretation: {
      label: 'Our assessment',
      description: 'Guidance based on research and analysis. Not vendor claims.',
      bgColor: 'bg-accent/5',
      borderColor: 'border-accent/20',
      icon: Info,
    },
  };

  const { label, description, bgColor, borderColor, icon: Icon } = config[type] || config.fact;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border cursor-help',
            bgColor,
            borderColor,
            type === 'interpretation' ? 'text-accent' : 'text-muted-foreground',
            className
          )}>
            {Icon && <Icon className="w-3 h-3" />}
            {label}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs">{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Confidence legend for comparison pages
export const ConfidenceLegend = ({ className }) => {
  return (
    <div className={cn('flex items-center gap-4 text-xs text-muted-foreground', className)}>
      <span className="font-medium">Data sources:</span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
        Vendor-provided
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-success" />
        Independently reviewed
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-warning" />
        Information unclear
      </span>
    </div>
  );
};
